// API Configuration
// Empty string = relative path → Vite proxy forwards to the backend.
// Set VITE_API_BASE_URL only when the frontend and API are on different origins
// (e.g. a deployed environment). Never set it to localhost in dev — that bypasses
// the Vite proxy and causes CORS failures.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Zeecore (Truth Layer) base URL. Empty = same origin: in production the edge
// routes ZeeCore paths, in dev the Vite proxy does. This is the only place the
// frontend decides where ZeeCore is; never fall back to a localhost URL here or
// in a service module — a production build would then call the user's own
// machine. vite.config.ts fails a production build that doesn't set it.
export const ZEECORE_BASE_URL = import.meta.env.VITE_ZEECORE_BASE_URL || '';

// Service type: 'logistics' | 'emergency_services' | undefined (defaults to logistics)
const DEFAULT_SERVICE_TYPE = 'logistics';

export const API_ENDPOINTS = {
  // Base URLs
  base: ZEECORE_BASE_URL,
  
  // Utilisation endpoints
  dashboardTimeSeries: (period: string, serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/utilisation/${period}/dashboard-time-series`,
  vehicleLevelCSV: (period: string, serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/utilisation/${period}/vehicle-level-csv`,
  vehicleTypeLevelCSV: (period: string, serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/utilisation/${period}/vehicle-type-level-csv`,
  stationLevelCSV: (period: string, serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/utilisation/${period}/station-level-csv`,
  busyStationsCSV: (period: string, serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/utilisation/${period}/busy-stations-csv`,
  vehicleLevelOperations: (period: string, serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/utilisation/${period}/vehicle-level-operations`,
  vehicleInterchangeabilityPairsCSV: (period: string, serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/utilisation/${period}/vehicle-interchangeability-pairs-csv`,
  vehicleTripPatterns: (period: string, serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/utilisation/${period}/vehicle-trip-patterns`,
  scope3Emissions: (period: string, serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/utilisation/${period}/scope3-emissions`,
  
  // Fleet endpoints
  fleetList: (serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/fleet/list`,
  
  // Config endpoints
  utilConfig: (serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/config/util-config`,
  benchmarkConfig: (serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/config/benchmark-config`,
  simEnvConfig: (serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/config/sim-env-config`,
  vehicleList: (serviceType?: string) =>
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/config/vehicle-list`,
  optifleetVehicleList: (serviceType?: string) =>
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/config/optifleet-vehicle-list`,
  
  // Geographic plot endpoints
  geographicPlotMergedMaps: (period: string, serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/geographic_plots/${period}/merged-maps`,
  geographicPlotServiceMap: (period: string, serviceType?: string) =>
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/geographic_plots/${period}/service-map`,
  geographicPlotRouteMap: (period: string, serviceType?: string) =>
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/geographic_plots/${period}/route-map`,
  geographicRouteData: (period: string, serviceType?: string) =>
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/analytics/geographic/${period}/route-data`,

  // Incident endpoints
  incidentCategoryDistribution: (period: string, serviceType?: string) =>
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/incidents/${period}/category-distribution`,
  incidentCountyDistribution: (period: string, serviceType?: string) =>
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/incidents/${period}/county-distribution`,
  incidentMetadata: (serviceType?: string) =>
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/incidents/metadata`,
  
  // Pumping endpoints
  pumpingSummary: (serviceType?: string) =>
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/pumping/summary`,
  pumpingSummaryJSON: (serviceType?: string) =>
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/pumping/summary-json`,
  pumpingOnSceneRatio: (serviceType?: string) =>
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/pumping/on-scene-ratio`,
  pumpingLocationCalls: (serviceType?: string) =>
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/pumping/location-calls`,
  pumpingFrequencyPlot: (serviceType?: string) =>
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/pumping/frequency-plot`,
  
  // Utility endpoints
  periods: (serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/utilisation/periods`,
  health: () => `${API_BASE_URL}/api/health`,
  
  // Optimization endpoints (Phase 1 MVP)
  optimizationResults: (suffix?: string) => 
    `${API_BASE_URL}/api/optimization/results${suffix ? `?suffix=${encodeURIComponent(suffix)}` : ''}`,
  optimizationAggregateCostReport: (folderName?: string) => 
    `${API_BASE_URL}/api/optimization/aggregate-cost-report${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`,
  optimizationSimulationSummary: (folderName?: string) => 
    `${API_BASE_URL}/api/optimization/simulation-summary${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`,
  optimizationInitialUtilization: (folderName?: string) => 
    `${API_BASE_URL}/api/optimization/initial-utilization${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`,
  optimizationFinalUtilization: (folderName?: string) => 
    `${API_BASE_URL}/api/optimization/final-utilization${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`,
  optimizationLog: (folderName?: string) => 
    `${API_BASE_URL}/api/optimization/optimization-log${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`,
  optimizationUtilization: (runId: string) => `${API_BASE_URL}/api/optimization/utilization/${runId}`,
  optimizationMetrics: (runId: string) => `${API_BASE_URL}/api/optimization/metrics/${runId}`,
  optimizationCostAnalysis: (runId: string) => `${API_BASE_URL}/api/optimization/cost-analysis/${runId}`,
  optimizationMetadata: (folderName?: string) => 
    `${API_BASE_URL}/api/optimization/metadata${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`,
  optimizationIncidentLog: (folderName?: string) => 
    `${API_BASE_URL}/api/optimization/incident-log${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`,
  optimizationEventLog: (type: 'initial' | 'optimized', runId?: string, folderName?: string) => {
    const params = new URLSearchParams();
    params.append('type', type);
    if (runId) params.append('runId', runId);
    if (folderName) params.append('folder', folderName);
    return `${API_BASE_URL}/api/optimization/event-log?${params.toString()}`;
  },
  optimizationDispatchComparison: (runId: string, folderName?: string) => {
    const params = new URLSearchParams();
    if (folderName) params.append('folder', folderName);
    return `${API_BASE_URL}/api/optimization/dispatch-comparison/${runId}?${params.toString()}`;
  },
  
  // Electrification endpoints
  electrificationResults: () => `${API_BASE_URL}/api/electrification/results`,
  electrificationFleetSummary: (folderName?: string) => 
    `${API_BASE_URL}/api/electrification/fleet-summary${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`,
  electrificationStationDetails: (folderName?: string) => 
    `${API_BASE_URL}/api/electrification/station-details${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`,
  electrificationVehicleDetails: (folderName?: string) => 
    `${API_BASE_URL}/api/electrification/vehicle-details${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`,
  electrificationGenerationSummary: (folderName?: string) => 
    `${API_BASE_URL}/api/electrification/generation-summary${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`,
  electrificationValidationMetrics: (folderName?: string) => 
    `${API_BASE_URL}/api/electrification/validation-metrics${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`,
  electrificationReportPDF: (folderName?: string) => 
    `${API_BASE_URL}/api/electrification/report-pdf${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`,
  electrificationMetadata: (folderName?: string) => 
    `${API_BASE_URL}/api/electrification/metadata${folderName ? `?folder=${encodeURIComponent(folderName)}` : ''}`,
  
  // Maintenance endpoints (no period - independent analysis)
  maintenanceRecords: (serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/maintenance/maintenance-records`,
  
  // Manual data logging endpoints for sub-contractors
  manualUtilizationSubmit: (serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/manual/utilization`,
  manualUtilizationGet: (serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/manual/utilization`,
  manualUtilizationDelete: (serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/manual/utilization`,
  manualCarbonSubmit: (serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/manual/carbon`,
  manualCarbonGet: (serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/manual/carbon`,
  manualCarbonDelete: (serviceType?: string) => 
    `${API_BASE_URL}/api/data/${serviceType || DEFAULT_SERVICE_TYPE}/manual/carbon`,

  // Decision Engine endpoints
  orders: () => `${API_BASE_URL}/api/orders`,
  qargoOrders: () => `${API_BASE_URL}/api/qargo/orders`, // Read-only: gets orders from pre-exported CSV/JSON files
  jigsawFuel: () => `${API_BASE_URL}/api/jigsaw/fuel`, // Read-only: gets fuel data from pre-exported CSV/JSON files
  // Geocoding endpoint - uses backend multi-provider geocoding with caching
  geocode: (query: string, country?: string) => {
    const params = new URLSearchParams();
    params.set('query', query);
    if (country) params.set('country', country);
    return `${API_BASE_URL}/api/geocode?${params.toString()}`;
  },
  // Geocoding autocomplete endpoint - provides address/postcode suggestions
  geocodeAutocomplete: (query: string, country?: string, limit?: number) => {
    const params = new URLSearchParams();
    params.set('query', query);
    if (country) params.set('country', country);
    if (limit) params.set('limit', String(limit));
    return `${API_BASE_URL}/api/geocode/autocomplete?${params.toString()}`;
  },
  // Postcode lookup endpoint - fast lookup from UK postcodes CSV (S3)
  postcodeLookup: (postcode: string) => {
    const params = new URLSearchParams();
    params.set('postcode', postcode);
    return `${API_BASE_URL}/api/postcode/lookup?${params.toString()}`;
  },
  // Batch postcode lookup endpoint - optimized for multiple lookups
  postcodeBatchLookup: () => `${API_BASE_URL}/api/postcode/batch-lookup`,
  // Run BigQuery export (no auth, for local/dev). Supports custom date range via query params.
  qargoRunExport: (start?: string, end?: string) => {
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    const qs = params.toString();
    return `${API_BASE_URL}/api/qargo/run-export${qs ? `?${qs}` : ''}`;
  },
  decide: () => `${API_BASE_URL}/api/decide`,
  decideAll: () => `${API_BASE_URL}/api/decide-all`,
  /** Legacy endpoint: list past decisions from app backend. */
  decisionsList: () => `${API_BASE_URL}/api/decisions`,
  decisions: {
    orderVehicleLinks: (orderId: string) => `${ZEECORE_BASE_URL}/decide/order-vehicle-links/${encodeURIComponent(orderId)}`,
    orderVehicleLinksBatch: () => `${ZEECORE_BASE_URL}/decide/order-vehicle-links/batch`,
  },
  planning: () => `${API_BASE_URL}/api/planning`,
  planningDownload: () => `${API_BASE_URL}/api/planning/download`,
  annotate: () => `${API_BASE_URL}/api/annotate`,
  emissions: () => `${API_BASE_URL}/api/emissions`,
  plannedOrders: () => `${API_BASE_URL}/api/planned-orders`,
  
  // Backloader – posted jobs (stored in S3 per organization)
  backloaderPostedJobs: () => `${API_BASE_URL}/api/backloader/posted-jobs`,

  // Marketplace endpoints
  marketplaceConfig: () => `${API_BASE_URL}/api/marketplace/config`,
  marketplaceOffers: () => `${API_BASE_URL}/api/marketplace/offers`,
  marketplaceNeeds: () => `${API_BASE_URL}/api/marketplace/needs`,
  marketplaceMatches: () => `${API_BASE_URL}/api/marketplace/matches`,
  marketplaceMatch: () => `${API_BASE_URL}/api/marketplace/match`,
  
  // Fleet endpoints
  fleetVehicles: () => `${API_BASE_URL}/api/fleet/vehicles`,
  
  // Scoring weights endpoints
  weights: () => `${API_BASE_URL}/api/weights`,
  
  // User management endpoints (admin only)
  users: () => `${API_BASE_URL}/api/users`,
  userPages: () => `${API_BASE_URL}/api/users/pages`,
  createUser: () => `${API_BASE_URL}/api/users`,
  updateUserPermissions: (userId: string) => `${API_BASE_URL}/api/users/${userId}/permissions`,
  updateUserPassword: (userId: string) => `${API_BASE_URL}/api/users/${userId}/password`,
  deleteUser: (userId: string) => `${API_BASE_URL}/api/users/${userId}`,
  
  // Fleet Control Centre - Aquarius Driver Compliance
  aquariusData: () => `${API_BASE_URL}/api/aquarius/data`,
  
  // Fleet Control Centre - Optifleet GPS Tracking
  optifleetData: (realtime = false) => 
    `${API_BASE_URL}/api/optifleet/${realtime ? 'realtime' : 'data'}`,
  
  // Fleet Control Centre - Supatrak GPS Tracking
  supatrakData: (realtime = false) => 
    `${API_BASE_URL}/api/dashboard/supatrak/realtime${realtime ? '?stream=true' : ''}`,
  
  // Profitability and fuel partitioned S3 APIs
  profitability: (start: string, end: string) => `${API_BASE_URL}/api/profitability?start=${start}&end=${end}`,
  /** Persisted profitability email schedule (backend cron + SMTP). Requires Cognito auth. */
  profitabilityReportSchedule: () => `${API_BASE_URL}/api/profitability/report-schedule`,
  profitabilityAgentQuery: () => `${API_BASE_URL}/api/profitability/agent/query`,
  profitabilityAgentQueryStream: () => `${API_BASE_URL}/api/profitability/agent/query/stream`,
  profitabilityMondayBrief: () => `${API_BASE_URL}/api/profitability/monday-brief`,
  fuel: (start: string, end: string) => `${API_BASE_URL}/api/fuel?start=${start}&end=${end}`,
  
  // Profitability V2 — Truth Layer (Zeecore) endpoints
  // Uses canonical events and graph edges, not S3 pipeline outputs.
  // All derived values (profit, total_cost) computed server-side with provenance.
  profitabilityV2: {
    mergedCosts: (startDate: string, endDate: string, orgId?: string) => {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      if (orgId) params.append('orgId', orgId);
      return `${ZEECORE_BASE_URL}/profitability/v2/merged-costs?${params.toString()}`;
    },
    mergedCostsFull: (startDate: string, endDate: string, orgId?: string) => {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      if (orgId) params.append('orgId', orgId);
      return `${ZEECORE_BASE_URL}/profitability/v2/merged-costs/full?${params.toString()}`;
    },
    status: (
      startDate?: string,
      endDate?: string,
      orgId?: string,
      opts?: { includeCoverage?: boolean; probeSources?: boolean },
    ) => {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (orgId) params.append('orgId', orgId);
      if (opts?.includeCoverage) params.append('includeCoverage', 'true');
      if (opts?.probeSources === false) params.append('probeSources', 'false');
      const qs = params.toString();
      return `${ZEECORE_BASE_URL}/profitability/v2/status${qs ? `?${qs}` : ''}`;
    },
    /** Monthly network charge per outbound consignment, computed from Xero (read only). */
    networkCostMonthlyRates: () => `${ZEECORE_BASE_URL}/profitability/v2/network-cost/monthly-rates`,
    costCheckMonthly: () => `${ZEECORE_BASE_URL}/profitability/v2/cost-check/monthly`,
    /** Months with stored vehicle-day profit (Custom period picker). */
    months: (orgId?: string) =>
      `${ZEECORE_BASE_URL}/profitability/v2/months${orgId ? `?orgId=${encodeURIComponent(orgId)}` : ''}`,
    coverage: (startDate: string, endDate: string, orgId?: string) => {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      if (orgId) params.append('orgId', orgId);
      return `${ZEECORE_BASE_URL}/profitability/v2/coverage?${params.toString()}`;
    },
    compute: () => `${ZEECORE_BASE_URL}/profitability/v2/compute`,
    computeOrders: () => `${ZEECORE_BASE_URL}/profitability/v2/compute-orders`,
    jobStatus: (jobId: string) => `${ZEECORE_BASE_URL}/profitability/v2/jobs/${encodeURIComponent(jobId)}`,
    cancelJob: (jobId: string) =>
      `${ZEECORE_BASE_URL}/profitability/v2/jobs/${encodeURIComponent(jobId)}/cancel`,
    jobsList: () => `${ZEECORE_BASE_URL}/profitability/v2/jobs`,
    config: () => `${ZEECORE_BASE_URL}/profitability/v2/config`,
    configDefaults: () => `${ZEECORE_BASE_URL}/profitability/v2/config/defaults`,
    configHistory: () => `${ZEECORE_BASE_URL}/profitability/v2/config/history`,
    configHistoryClear: () => `${ZEECORE_BASE_URL}/profitability/v2/config/history/clear`,
    mergedOrders: (startDate: string, endDate: string) =>
      `${ZEECORE_BASE_URL}/profitability/v2/merged-orders?startDate=${startDate}&endDate=${endDate}`,
    /** Company KPIs from ingested Xero books (Phase 2). */
    profitKpis: (startDate: string, endDate: string) => {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      return `${ZEECORE_BASE_URL}/profitability/v2/profit-kpis?${params.toString()}`;
    },
    customerMonthly: (startDate: string, endDate: string) =>
      `${ZEECORE_BASE_URL}/profitability/v2/customer-profitability-monthly?startDate=${startDate}&endDate=${endDate}`,
    customerDaily: (startDate: string, endDate: string) =>
      `${ZEECORE_BASE_URL}/profitability/v2/customer-profitability-daily?startDate=${startDate}&endDate=${endDate}`,
    telematicsStops: (startDate: string, endDate: string) =>
      `${ZEECORE_BASE_URL}/profitability/v2/telematics-delivery-stops?startDate=${startDate}&endDate=${endDate}`,
    telematicsSegments: (startDate: string, endDate: string) =>
      `${ZEECORE_BASE_URL}/profitability/v2/telematics-route-segments?startDate=${startDate}&endDate=${endDate}`,
    importHistorical: () => `${ZEECORE_BASE_URL}/profitability/v2/import-historical`,
  },

  // Utilisation V2 — Truth Layer (zeecore)
  utilisationV2: {
    vehicleDays: (startDate: string, endDate: string) => {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      return `${ZEECORE_BASE_URL}/utilisation/v2/vehicle-days?${params.toString()}`;
    },
    status: (startDate: string, endDate: string) => {
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      return `${ZEECORE_BASE_URL}/utilisation/v2/status?${params.toString()}`;
    },
    compute: () => `${ZEECORE_BASE_URL}/utilisation/v2/compute`,
    jobStatus: (jobId: string) =>
      `${ZEECORE_BASE_URL}/utilisation/v2/jobs/${encodeURIComponent(jobId)}`,
    jobsList: () => `${ZEECORE_BASE_URL}/utilisation/v2/jobs`,
    config: () => `${ZEECORE_BASE_URL}/utilisation/v2/config`,
    configHistory: () => `${ZEECORE_BASE_URL}/utilisation/v2/config/history`,
    configHistoryClear: () => `${ZEECORE_BASE_URL}/utilisation/v2/config/history/clear`,
  },

  // Orders daily projection — Control Centre KPIs (on-demand Iceberg)
  ordersV1: {
    /** Whether this org gets Order Flow Intelligence — decided by zeecore, read-only. */
    availability: () => `${ZEECORE_BASE_URL}/orders/v1/availability`,
    daily: (
      startDate: string,
      endDate: string,
      opts?: { includeSummary?: boolean; includeBacklog?: boolean; includeStale?: boolean },
    ) => {
      const params = new URLSearchParams({ startDate, endDate });
      if (opts?.includeSummary) params.set('includeSummary', '1');
      if (opts?.includeBacklog) params.set('includeBacklog', '1');
      if (opts?.includeBacklog && opts?.includeStale) params.set('includeStale', '1');
      return `${ZEECORE_BASE_URL}/orders/v1/daily?${params.toString()}`;
    },
    dailySummary: (startDate: string, endDate: string) => {
      const params = new URLSearchParams({ startDate, endDate });
      return `${ZEECORE_BASE_URL}/orders/v1/daily/summary?${params.toString()}`;
    },
    dailySeries: (startDate: string, endDate: string) => {
      const params = new URLSearchParams({ startDate, endDate });
      return `${ZEECORE_BASE_URL}/orders/v1/daily/series?${params.toString()}`;
    },
    /** Week / month / quarter / year figures, served from stored period records. */
    periods: (grain: string, from: string, to: string, view: 'due' | 'done' = 'due') => {
      const params = new URLSearchParams({ grain, view, from, to });
      return `${ZEECORE_BASE_URL}/orders/v1/periods?${params.toString()}`;
    },
    config: () => `${ZEECORE_BASE_URL}/orders/v1/config`,
    /** This user's saved Orders page layout (section order, expanded sections, ETA warning). */
    uiPreferences: () => `${ZEECORE_BASE_URL}/orders/v1/ui-preferences`,
  },

  // GPS site visits (zeecore /telematics/v1): van stops + non-delivery idle per day, trail on request.
  telematicsV1: {
    vehicleDayStops: (date: string) =>
      `${ZEECORE_BASE_URL}/telematics/v1/vehicle-day-stops?${new URLSearchParams({ date }).toString()}`,
    vehicleTrail: (vehicleEntityId: string, from: string, to: string) =>
      `${ZEECORE_BASE_URL}/telematics/v1/vehicles/${encodeURIComponent(vehicleEntityId)}/trail?${new URLSearchParams({ from, to }).toString()}`,
  },

  // Ingestion Management endpoints (Zeecore - Truth Layer)
  ingestionManage: {
    status: () => `${ZEECORE_BASE_URL}/ingestion/manage/status`,
    /** Per-feed health: failing vs overdue, and refused runs. */
    feeds: () => `${ZEECORE_BASE_URL}/ingestion/manage/feeds`,
    backfill: () => `${ZEECORE_BASE_URL}/ingestion/manage/backfill`,
    incremental: () => `${ZEECORE_BASE_URL}/ingestion/manage/incremental`,
    jobsList: () => `${ZEECORE_BASE_URL}/ingestion/manage/jobs`,
    jobStatus: (jobId: string) => `${ZEECORE_BASE_URL}/ingestion/manage/jobs/${encodeURIComponent(jobId)}`,
    cancelJob: (jobId: string) => `${ZEECORE_BASE_URL}/ingestion/manage/jobs/${encodeURIComponent(jobId)}/cancel`,
    pipelineStatus: () => `${ZEECORE_BASE_URL}/ingestion/manage/pipeline-status`,
    dlqRedrive: () => `${ZEECORE_BASE_URL}/ingestion/manage/pipeline/dlq-redrive`,
    reprocess: () => `${ZEECORE_BASE_URL}/ingestion/manage/reprocess`,
    identityIndexRebuild: () => `${ZEECORE_BASE_URL}/ingestion/manage/identity-index/rebuild`,
  },

  /** Declared / Same Day TMS — push uploads (Day Sheet Excel, Same Day Tracker CSV) */
  declaredIngest: {
    dailyPlan: () => `${ZEECORE_BASE_URL}/ingest/declared/daily-plan`,
    sameDayTracker: () => `${ZEECORE_BASE_URL}/ingest/declared/same-day-tracker`,
    /** Weekly Keyfuels diesel ppl — stores PDF (when present) + structured rate */
    keyfuelsDieselPrice: () => `${ZEECORE_BASE_URL}/ingest/declared/keyfuels-diesel-price`,
  },

  /** Eflex / Keyfuels fuel card CSV (or Excel converted client-side to CSV) */
  eflexIngest: {
    transactions: () => `${ZEECORE_BASE_URL}/ingest/eflex/transactions`,
  },

  /** Xero Accounting OAuth (status + reconnect for Data Import) */
  xeroOAuth: {
    status: () => `${ZEECORE_BASE_URL}/api/oauth/xero/status`,
    /** JSON authorize URL for SPA reconnect (`Accept: application/json`). */
    start: () => `${ZEECORE_BASE_URL}/api/oauth/xero/start?format=json`,
  },
  
  // Fleet Intelligence endpoints (Zeecore - Truth Layer)
  fleetIntelligence: {
    config: {
      depots: () => `${ZEECORE_BASE_URL}/fleet-intelligence/config/depots`,
      fleetCostAssumptions: () =>
        `${ZEECORE_BASE_URL}/fleet-intelligence/config/fleet-cost-assumptions`,
      fleetCostAssumptionsDefaults: () =>
        `${ZEECORE_BASE_URL}/fleet-intelligence/config/fleet-cost-assumptions/defaults`,
      fleetCostAssumptionsHistory: () =>
        `${ZEECORE_BASE_URL}/fleet-intelligence/config/fleet-cost-assumptions/history`,
      fleetCostAssumptionsHistoryClear: () =>
        `${ZEECORE_BASE_URL}/fleet-intelligence/config/fleet-cost-assumptions/history/clear`,
      systemHealthDaily: (days = 7) =>
        `${ZEECORE_BASE_URL}/fleet-intelligence/config/system-health-daily?days=${encodeURIComponent(String(days))}`,
      systemHealthDailyWrite: () => `${ZEECORE_BASE_URL}/fleet-intelligence/config/system-health-daily`,
    },
    branding: {
      profilePicture: () => `${ZEECORE_BASE_URL}/fleet-intelligence/branding/profile-picture`,
    },
    vehicles: {
      list: (
        timeRange?: { from: string; to: string },
        options?: { enrichment?: 'skip' | 'full'; refresh?: boolean },
      ) => {
        const params = new URLSearchParams();
        if (timeRange) {
          params.set('from', timeRange.from);
          params.set('to', timeRange.to);
        }
        if (options?.enrichment === 'skip') {
          params.set('enrichment', 'skip');
        }
        // Skip the server's roster cache (Assets Refresh).
        if (options?.refresh) {
          params.set('refresh', '1');
        }
        const qs = params.toString();
        return `${ZEECORE_BASE_URL}/fleet-intelligence/vehicles/list${qs ? `?${qs}` : ''}`;
      },
      /** Lightweight type/payload labels for Configuration quantity (skips full Assets list). */
      classLabels: () => `${ZEECORE_BASE_URL}/fleet-intelligence/vehicles/class-labels`,
      /** Manual vehicle-detail edits → Truth Layer VehicleAttributesDeclared events */
      attributes: () => `${ZEECORE_BASE_URL}/fleet-intelligence/vehicles/attributes`,
      attributesHistory: () => `${ZEECORE_BASE_URL}/fleet-intelligence/vehicles/attributes/history`,
      attributesHistoryClear: () =>
        `${ZEECORE_BASE_URL}/fleet-intelligence/vehicles/attributes/history/clear`,
      /** Trust stamps for Estimated/Asserted/Confirmed cell colours (Config Confirm rates analogue). */
      attributesConfirmations: () =>
        `${ZEECORE_BASE_URL}/fleet-intelligence/vehicles/attributes/confirmations`,
      activityByDay: (days = 7) =>
        `${ZEECORE_BASE_URL}/fleet-intelligence/vehicles/activity-by-day?days=${encodeURIComponent(String(days))}`,
      movements: (entityId: string, timeRange: { from: string; to: string }) => {
        const params = `?from=${encodeURIComponent(timeRange.from)}&to=${encodeURIComponent(timeRange.to)}`;
        return `${ZEECORE_BASE_URL}/fleet-intelligence/vehicles/${encodeURIComponent(entityId)}/movements${params}`;
      },
      /** Vehicle Movement Timeline projection (truth layer) */
      movementTimeline: (entityId: string, options: { from: string; to: string; asOf?: string; includePoints?: boolean }) => {
        const params = new URLSearchParams();
        params.append('from', options.from);
        params.append('to', options.to);
        if (options.asOf) params.append('asOf', options.asOf);
        if (options.includePoints !== undefined) params.append('includePoints', String(options.includePoints));
        return `${ZEECORE_BASE_URL}/truth/vehicles/${encodeURIComponent(entityId)}/movement?${params.toString()}`;
      },
    },
    orders: {
      list: (
        timeRange?: { from: string; to: string },
        options?: { tenantId?: string; skipSameDayConfirm?: boolean },
      ) => {
        const params = new URLSearchParams();
        if (timeRange) {
          params.append('from', timeRange.from);
          params.append('to', timeRange.to);
        }
        if (options?.tenantId) {
          params.append('tenantId', options.tenantId);
        }
        if (options?.skipSameDayConfirm) {
          params.append('skipSameDayConfirm', 'true');
        }
        const queryString = params.toString();
        return `${ZEECORE_BASE_URL}/fleet-intelligence/orders/list${queryString ? `?${queryString}` : ''}`;
      },
      /** Iceberg day rollup for Control Centre charts (independent of ingest lookback) */
      activityByDay: (days = 7) =>
        `${ZEECORE_BASE_URL}/fleet-intelligence/orders/activity-by-day?days=${encodeURIComponent(String(days))}`,
      movements: (entityId: string, timeRange: { from: string; to: string }) => {
        const params = `?from=${encodeURIComponent(timeRange.from)}&to=${encodeURIComponent(timeRange.to)}`;
        return `${ZEECORE_BASE_URL}/fleet-intelligence/orders/${encodeURIComponent(entityId)}/movements${params}`;
      },
    },
    /** Per-order Palletways live status (conStatusByTrackingId) — Control Centre row refresh */
    palletwaysConsignmentStatus: () => `${ZEECORE_BASE_URL}/ingest/palletways/consignment-status`,
    /** Server-side backfill for consignments missing getconsignment detail — Data Import page */
    palletwaysDetailBackfillStart: () => `${ZEECORE_BASE_URL}/ingest/palletways/backfill-details/start`,
    palletwaysDetailBackfillStatus: () => `${ZEECORE_BASE_URL}/ingest/palletways/backfill-details/status`,
    palletwaysAvailableServicesSeed: () => `${ZEECORE_BASE_URL}/ingest/palletways/available-services/seed`,
    palletwaysAvailableServicesStatus: () => `${ZEECORE_BASE_URL}/ingest/palletways/available-services/status`,
    /** Fleet Control Centre — aggregated KPI overview from truth tables */
    summary: (timeRange: { from: string; to: string }) =>
      `${ZEECORE_BASE_URL}/fleet-intelligence/summary?from=${encodeURIComponent(timeRange.from)}&to=${encodeURIComponent(timeRange.to)}`,
    /** Fuel analytics section — maps to JigsawData shape */
    fuelSummary: (timeRange: { from: string; to: string }) =>
      `${ZEECORE_BASE_URL}/fleet-intelligence/fuel/summary?from=${encodeURIComponent(timeRange.from)}&to=${encodeURIComponent(timeRange.to)}`,
    /** When fuel transactions were last uploaded and the fuel price last updated */
    fuelDataCurrentAsOf: () => `${ZEECORE_BASE_URL}/fleet-intelligence/fuel/data-current-as-of`,
    /** Samsara fuel & energy efficiency per vehicle and driver (UK days) */
    fuelEfficiency: (timeRange: { from: string; to: string }) =>
      `${ZEECORE_BASE_URL}/fleet-intelligence/fuel/efficiency?from=${encodeURIComponent(timeRange.from)}&to=${encodeURIComponent(timeRange.to)}`,
    /** Declared weekly supplier ppl (FuelPriceDeclared) — Control Centre Fuel intake history */
    fuelPrices: (timeRange: { from: string; to: string }) =>
      `${ZEECORE_BASE_URL}/fleet-intelligence/fuel/prices?from=${encodeURIComponent(timeRange.from)}&to=${encodeURIComponent(timeRange.to)}`,
    /** Driver compliance section — maps to AquariusData shape */
    /** `fresh` skips the server's 45-second response cache (a re-read because new data landed). */
    driversSummary: (timeRange: { from: string; to: string }, opts?: { fresh?: boolean }) =>
      `${ZEECORE_BASE_URL}/fleet-intelligence/drivers/summary?from=${encodeURIComponent(timeRange.from)}&to=${encodeURIComponent(timeRange.to)}${opts?.fresh ? '&refresh=1' : ''}`,
    /** Driver tab "Data current as of" alone (feed health; cheap, uncached) */
    driversDataCurrentAsOf: () => `${ZEECORE_BASE_URL}/fleet-intelligence/drivers/data-current-as-of`,
    /** Assets → Drivers: roster with declared qualifications, suggestions and unmatched names. */
    driversRoster: (opts?: { fresh?: boolean }) =>
      `${ZEECORE_BASE_URL}/fleet-intelligence/drivers/roster${opts?.fresh ? '?refresh=1' : ''}`,
    /** Driver qualification edits → DriverAttributesDeclared events */
    driversAttributes: () => `${ZEECORE_BASE_URL}/fleet-intelligence/drivers/attributes`,
    /** Link an unmatched source driver name to a driver (identity claim + decision record) */
    driversNameLinks: () => `${ZEECORE_BASE_URL}/fleet-intelligence/drivers/name-links`,
    /** Who resolved or reopened each drivers' hours violation (GET with from/to; POST one review) */
    driversViolationReviews: (timeRange?: { from: string; to: string }) =>
      `${ZEECORE_BASE_URL}/fleet-intelligence/drivers/violation-reviews${timeRange ? `?from=${encodeURIComponent(timeRange.from)}&to=${encodeURIComponent(timeRange.to)}` : ''}`,
    /** Daily walkaround check vs first drive (Driver tab) */
    driversCheckCompliance: (timeRange: { from: string; to: string }) =>
      `${ZEECORE_BASE_URL}/fleet-intelligence/drivers/check-compliance?from=${encodeURIComponent(timeRange.from)}&to=${encodeURIComponent(timeRange.to)}`,
    /** Maintenance section — faults, defects, check compliance, VOR */
    maintenanceSummary: (timeRange: { from: string; to: string }) =>
      `${ZEECORE_BASE_URL}/fleet-intelligence/maintenance/summary?from=${encodeURIComponent(timeRange.from)}&to=${encodeURIComponent(timeRange.to)}`,
    /**
     * Maintenance Schedule sub-view — overdue, due-soon and scheduled
     * obligations. The range bounds completed work only; outstanding
     * obligations are always returned in full.
     */
    maintenanceSchedule: (timeRange: { from: string; to: string }) =>
      `${ZEECORE_BASE_URL}/fleet-intelligence/maintenance/schedule?from=${encodeURIComponent(timeRange.from)}&to=${encodeURIComponent(timeRange.to)}`,
    /** Deterministic Daily Driver / Maintenance Brief */
    dailySafetyBrief: (timeRange: { from: string; to: string; scope?: 'driver' | 'maintenance' }) =>
      `${ZEECORE_BASE_URL}/fleet-intelligence/daily-safety-brief?from=${encodeURIComponent(timeRange.from)}&to=${encodeURIComponent(timeRange.to)}${timeRange.scope ? `&scope=${encodeURIComponent(timeRange.scope)}` : ''}`,
    /** Telematics section — last-known positions from truth layer, maps to OptifleetData shape */
    vehiclesSummary: (timeRange: { from: string; to: string }) =>
      `${ZEECORE_BASE_URL}/fleet-intelligence/vehicles/summary?from=${encodeURIComponent(timeRange.from)}&to=${encodeURIComponent(timeRange.to)}`,
    /** Real-time vehicle positions fetched live from configured telematics APIs (Optifleet/Supatrak) */
    vehiclesLive: () => `${ZEECORE_BASE_URL}/fleet-intelligence/vehicles/live`,
    /** Tenant-scoped depot / hub markers for Control Centre map */
    depots: () => `${ZEECORE_BASE_URL}/fleet-intelligence/depots`,
    /** Fleet Report Geographic Insights — truth-layer GPS heatmap/coverage */
    geographic: {
      months: () => `${ZEECORE_BASE_URL}/fleet-intelligence/geographic/months`,
      heatmap: (timeRange: { from: string; to: string }) =>
        `${ZEECORE_BASE_URL}/fleet-intelligence/geographic/heatmap?from=${encodeURIComponent(timeRange.from)}&to=${encodeURIComponent(timeRange.to)}`,
      route: (timeRange: { from: string; to: string }) =>
        `${ZEECORE_BASE_URL}/fleet-intelligence/geographic/route?from=${encodeURIComponent(timeRange.from)}&to=${encodeURIComponent(timeRange.to)}`,
    },
  },
  
  // Order Projection endpoints (Zeecore - Truth Layer)
  orderProjection: {
    timeline: (orderId: string) =>
      `${ZEECORE_BASE_URL}/projection/order/${encodeURIComponent(orderId)}/timeline`,
    movementOverlay: (orderId: string) =>
      `${ZEECORE_BASE_URL}/projection/order/${encodeURIComponent(orderId)}/movement-overlay`,
    investigationCase: (orderId: string) =>
      `${ZEECORE_BASE_URL}/projection/order/${encodeURIComponent(orderId)}/investigation-case`,
  },

  // Vehicle Projection endpoints (Zeecore - Truth Layer)
  vehicleProjection: {
    dayCase: (vehicleId: string, date: string) =>
      `${ZEECORE_BASE_URL}/projection/vehicle/${encodeURIComponent(vehicleId)}/day-case/${encodeURIComponent(date)}`,
    windowProjection: (vehicleId: string, from: string, to: string) =>
      `${ZEECORE_BASE_URL}/projection/vehicle/${encodeURIComponent(vehicleId)}/window?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    windowProjectionByDate: (vehicleId: string, date: string) =>
      `${ZEECORE_BASE_URL}/projection/vehicle/${encodeURIComponent(vehicleId)}/window?date=${encodeURIComponent(date)}`,
  },

  // Maintenance (Truth Layer - Zeecore) endpoints
  maintenanceTruth: {
    summary: () => `${ZEECORE_BASE_URL}/maintenance/summary`,
    events: (options?: { page?: number; pageSize?: number; maintenanceType?: string; facilityLocation?: string; vehicleId?: string; fromDate?: string; toDate?: string }) => {
      const params = new URLSearchParams();
      if (options?.page) params.append('page', String(options.page));
      if (options?.pageSize) params.append('pageSize', String(options.pageSize));
      if (options?.maintenanceType) params.append('maintenanceType', options.maintenanceType);
      if (options?.facilityLocation) params.append('facilityLocation', options.facilityLocation);
      if (options?.vehicleId) params.append('vehicleId', options.vehicleId);
      if (options?.fromDate) params.append('fromDate', options.fromDate);
      if (options?.toDate) params.append('toDate', options.toDate);
      const qs = params.toString();
      return `${ZEECORE_BASE_URL}/maintenance/events${qs ? `?${qs}` : ''}`;
    },
    costBreakdown: () => `${ZEECORE_BASE_URL}/maintenance/cost-breakdown`,
    trends: (granularity?: string) => {
      const params = new URLSearchParams();
      if (granularity) params.append('granularity', granularity);
      const qs = params.toString();
      return `${ZEECORE_BASE_URL}/maintenance/trends${qs ? `?${qs}` : ''}`;
    },
    vehicle: (vehicleId: string) => `${ZEECORE_BASE_URL}/maintenance/vehicle/${encodeURIComponent(vehicleId)}`,
    defects: () => `${ZEECORE_BASE_URL}/maintenance/defects`,
    upcoming: () => `${ZEECORE_BASE_URL}/maintenance/upcoming`,
    defectChecks: (options?: { fromDate?: string; toDate?: string }) => {
      const params = new URLSearchParams();
      if (options?.fromDate) params.append('fromDate', options.fromDate);
      if (options?.toDate) params.append('toDate', options.toDate);
      const qs = params.toString();
      return `${ZEECORE_BASE_URL}/maintenance/defect-checks${qs ? `?${qs}` : ''}`;
    },
    vorAssets: () => `${ZEECORE_BASE_URL}/maintenance/vor-assets`,
    filterOptions: () => `${ZEECORE_BASE_URL}/maintenance/filter-options`,
  },

  // Historical Reconstruction endpoints (Zeecore - Truth Layer)
  reconstruction: {
    trigger: () => `${ZEECORE_BASE_URL}/reconstruction/historical`,
    jobs: () => `${ZEECORE_BASE_URL}/reconstruction/jobs`,
    jobStatus: (jobId: string) => `${ZEECORE_BASE_URL}/reconstruction/jobs/${encodeURIComponent(jobId)}`,
    orderEvidence: (orderId: string) => `${ZEECORE_BASE_URL}/reconstruction/order/${encodeURIComponent(orderId)}/evidence`,
    orderDerivedEvidence: (orderId: string) => `${ZEECORE_BASE_URL}/reconstruction/order/${encodeURIComponent(orderId)}/derived-evidence`,
    vehicleDateResolved: (vehicleEntityId: string, date: string) => `${ZEECORE_BASE_URL}/reconstruction/vehicle/${encodeURIComponent(vehicleEntityId)}/${encodeURIComponent(date)}/resolved`,
    orderExplain: (orderId: string) => `${ZEECORE_BASE_URL}/reconstruction/order/${encodeURIComponent(orderId)}/explain`,
  },

  // Truth Browser endpoints (Zeecore - Data visibility for truth layer)
  truthBrowser: {
    tables: () => `${ZEECORE_BASE_URL}/truth-browser/tables`,
    stats: (options?: { refresh?: boolean }) => {
      const params = new URLSearchParams();
      if (options?.refresh) params.set('refresh', 'true');
      const qs = params.toString();
      return `${ZEECORE_BASE_URL}/truth-browser/stats${qs ? `?${qs}` : ''}`;
    },
    data: (tableKey: string, options?: { 
      page?: number; 
      pageSize?: number; 
      fromDate?: string; 
      toDate?: string;
      search?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      view?: 'canonical' | 'claims';
    }) => {
      const params = new URLSearchParams();
      if (options?.page) params.append('page', String(options.page));
      if (options?.pageSize) params.append('pageSize', String(options.pageSize));
      if (options?.fromDate) params.append('fromDate', options.fromDate);
      if (options?.toDate) params.append('toDate', options.toDate);
      if (options?.search) params.append('search', options.search);
      if (options?.sortBy) params.append('sortBy', options.sortBy);
      if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
      if (options?.view) params.append('view', options.view);
      const queryString = params.toString();
      return `${ZEECORE_BASE_URL}/truth-browser/data/${encodeURIComponent(tableKey)}${queryString ? `?${queryString}` : ''}`;
    },
    relationships: (tableKey: string) => 
      `${ZEECORE_BASE_URL}/truth-browser/relationships/${encodeURIComponent(tableKey)}`,
    related: (tableKey: string, entityId: string) => 
      `${ZEECORE_BASE_URL}/truth-browser/related/${encodeURIComponent(tableKey)}/${encodeURIComponent(entityId)}`,
    graphRelationships: (entityType: string, entityId: string, options?: { from?: string; to?: string; relationshipType?: string }) => {
      const params = new URLSearchParams();
      if (options?.from) params.append('from', options.from);
      if (options?.to) params.append('to', options.to);
      if (options?.relationshipType) params.append('relationshipType', options.relationshipType);
      const queryString = params.toString();
      return `${ZEECORE_BASE_URL}/truth-browser/graph-relationships/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}${queryString ? `?${queryString}` : ''}`;
    },
  },
};

export default API_BASE_URL;
